import { useCallback, useEffect, useMemo, useState } from 'react'
import { useGetQuery, useDebouncedCallback } from 'hooks'

// Generic search-as-you-type input. Two modes, chosen by which props are passed:
//   - local filtering: pass `items` (the full candidate list) and it filters client-side
//   - remote search:   pass `searchUrl` + `extractResults` and it debounces a GET as you type
// Either way, `items` is also used to resolve the label for the current `value` on mount/change
// (for remote mode, pass a singleton `[selectedItem]` — see admin feature-flags override picker).
export const SearchTypeahead = ({
  value,
  items = [],
  searchUrl,
  extractResults,
  minChars = 1,
  debounceMs = 250,
  getKey = (item) => item.id,
  getLabel,
  renderItem,
  onSelect,
  placeholder = 'Search…',
  maxResults = 20,
  inputStyle,
  dropdownStyle,
  itemStyle,
  disabled
}) => {
  const [input, setInput] = useState(() => {
    const selected = items.find((i) => getKey(i) === value)
    return selected ? getLabel(selected) : ''
  })
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const item = items.find((i) => getKey(i) === value)
    setInput(item ? getLabel(item) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items])

  const remote = useGetQuery({ url: searchUrl ? `${searchUrl}?q=` : null, enabled: false })
  const { data: remoteData, setPath, setEnabled, isFetching } = remote || {}

  const triggerSearch = useCallback((term) => {
    setPath(`${searchUrl}?q=${encodeURIComponent(term)}`)
    setEnabled(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUrl])
  const debouncedTriggerSearch = useDebouncedCallback(triggerSearch, debounceMs)

  useEffect(() => {
    if (!searchUrl) return
    const term = input.trim()
    if (term.length < minChars) { setEnabled(false); return }
    debouncedTriggerSearch(term)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, searchUrl, minChars, debouncedTriggerSearch])

  const localFiltered = useMemo(() => {
    if (searchUrl) return []
    const term = input.trim().toLowerCase()
    const list = term ? items.filter((i) => getLabel(i).toLowerCase().includes(term)) : items
    return list.slice(0, maxResults)
  }, [input, items, searchUrl, maxResults, getLabel])

  const suggestions = searchUrl ? (extractResults?.(remoteData) ?? []).slice(0, maxResults) : localFiltered

  const resetToSelected = () => {
    const item = items.find((i) => getKey(i) === value)
    setInput(item ? getLabel(item) : '')
  }

  const select = (item) => {
    onSelect(getKey(item), item)
    setInput(getLabel(item))
    setOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={input}
        disabled={disabled}
        onChange={(e) => { setInput(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => {
          setOpen(false)
          if (!input.trim()) { onSelect(null, null); return }
          resetToSelected()
        }, 150)}
        placeholder={searchUrl && isFetching ? 'Searching…' : placeholder}
        style={inputStyle}
      />
      {open && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 200,
            overflowY: 'auto',
            zIndex: 100,
            ...dropdownStyle
          }}
        >
          {suggestions.map((item) => (
            <div key={getKey(item)} onMouseDown={() => select(item)} style={itemStyle}>
              {renderItem ? renderItem(item) : getLabel(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
